import { getDefaultPaymentDate } from "./utils";
import { getRecord } from "../../api/useRecord";
import { getOrderInvoices } from "../../api/moloni/useOrderInvoice";
import { getInvoiceSchedules } from "../../api/moloni/useInvoiceSchedules";
import { calculateInvoicedNet } from "./moloni/invoiceDocumentsUtils";
import { calculatePendingScheduledTotal } from "./moloni/scheduled-invoices/utils";
import { formatCurrency } from "../../utils/formatCurrency";

export type ValidationResult = {
    isValid: boolean;
    message?: string;
    requiresConfirmation?: boolean;
};

type OrderData = {
    order_date: string;
    order_items: Array<any>;
    treatment: { id: number };
    payment_status: string;
    clinic: { id: number };
    order_payments?: Array<{
        payment_method: string;
        payment_date: string;
        amount: number;
    }>;
};

export const validateOrder = async ({ data, orderTotal, orderId, onValidationError }: ValidateOrderParams) => {
    const baseValidators = [
        () => validateRequiredFields(data),
        () => validateClinicMatch(data),
        () => validateOrderItems(data),
        () => validateInvoicedBelowTotal(orderId, orderTotal),
    ];

    const paymentValidators = [
        () => validatePaymentsExist(data),
        () => validatePaymentMethods(data),
        () => validatePaymentDates(data),
        () => validatePaymentsTotal(data, orderTotal),
    ];

    const validators = data.payment_status === "paid" 
        ? [...baseValidators, ...paymentValidators] 
        : baseValidators;

    for (const validator of validators) {
        const validation = await validator();
        if (!validation.isValid) {
            const shouldContinue = await onValidationError(validation);
            if (!shouldContinue) return false;
        }
    }

    return true;
};


export const validateRequiredFields = (data: OrderData): ValidationResult => {
    const hasAllRequiredFields = data.order_date && data.order_items && data.treatment && data.payment_status && data.clinic;

    if (!hasAllRequiredFields) {
        return { isValid: false, message: "Preencha todos os campos obrigatórios" };
    }

    return { isValid: true };
};

export const validateOrderItems = (data: OrderData): ValidationResult => {
    if (data.order_items.length === 0) {
        return { isValid: false, message: "Adicione pelo menos um serviço" };
    }

    return { isValid: true };
};

export const validateClinicMatch = async (data: OrderData): Promise<ValidationResult> => {
    const treatment = await getRecord("treatments", data.treatment?.id, "*.*");
    const clientClinicId = treatment.item?.client?.clinic_id;

    if (data.clinic?.id !== clientClinicId) {
        return {
            isValid: false,
            message: "O cliente está associado a uma clínica diferente da clínica selecionada. Deseja continuar mesmo assim?",
            requiresConfirmation: true,
        };
    }

    return { isValid: true };
};

export const validateInvoicedBelowTotal = async (orderId: number | undefined, orderTotal: number): Promise<ValidationResult> => {
    if (!orderId) return { isValid: true };

    try {
        const [invoiceDocuments, schedules] = await Promise.all([
            getOrderInvoices(orderId),
            getInvoiceSchedules(orderId, true),
        ]);
        const invoicedNet = calculateInvoicedNet(invoiceDocuments);
        const pendingScheduledTotal = calculatePendingScheduledTotal(schedules);
        const projectedTotal = invoicedNet + pendingScheduledTotal;

        if (projectedTotal <= orderTotal + 0.01) return { isValid: true };

        if (pendingScheduledTotal > 0) {
            return {
                isValid: false,
                message: `Esta compra tem ${formatCurrency(invoicedNet)} faturados e ${formatCurrency(pendingScheduledTotal)} agendados por faturar, acima do novo total da compra (${formatCurrency(orderTotal)}). Ajuste primeiro os agendamentos de faturas.`,
            };
        }

        return {
            isValid: false,
            message: `Esta compra já tem ${formatCurrency(invoicedNet)} faturados na Moloni, acima do novo total da compra (${formatCurrency(orderTotal)}). Deseja continuar mesmo assim?`,
            requiresConfirmation: true,
        };
    } catch {
        return { isValid: true };
    }
};

export const validatePaymentsExist = (data: OrderData): ValidationResult => {
    if (!data.order_payments || data.order_payments.length === 0) {
        return { isValid: false, message: "Adicione pelo menos um método de pagamento" };
    }

    return { isValid: true };
};

export const validatePaymentMethods = (data: OrderData): ValidationResult => {
    const hasEmptyPaymentMethod = data.order_payments?.some((payment) => !payment.payment_method);

    if (hasEmptyPaymentMethod) {
        return { isValid: false, message: "Todos os pagamentos devem ter um método de pagamento selecionado" };
    }

    return { isValid: true };
};

export const validatePaymentDates = (data: OrderData): ValidationResult => {
    const todayDate = getDefaultPaymentDate();
    const hasPastPaymentDate = data.order_payments?.some((payment) => payment.payment_date < todayDate);

    if (hasPastPaymentDate) {
        return {
            isValid: false,
            message: "Existem pagamentos com data anterior a hoje. Deseja continuar?",
            requiresConfirmation: true,
        };
    }

    return { isValid: true };
};

export const validatePaymentsTotal = (data: OrderData, orderTotal: number): ValidationResult => {
    const paymentsTotal = data.order_payments?.reduce((acc, payment) => acc + Number(payment.amount || 0), 0) || 0;
    const difference = Math.abs(paymentsTotal - orderTotal);

    if (difference > 0.01) {
        const message =
            paymentsTotal > orderTotal
                ? `O total dos pagamentos (€${paymentsTotal.toFixed(2)}) excede o total da compra (€${orderTotal.toFixed(2)})`
                : `O total dos pagamentos (€${paymentsTotal.toFixed(2)}) é inferior ao total da compra (€${orderTotal.toFixed(
                      2
                  )})`;

        return { isValid: false, message };
    }

    return { isValid: true };
};

type ValidateOrderParams = {
    data: OrderData;
    orderTotal: number;
    orderId?: number;
    onValidationError: (result: ValidationResult) => Promise<boolean>;
};
